import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Doughnut, Line } from 'react-chartjs-2';
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Heading,
    HStack,
    Input,
    Select,
    SimpleGrid,
    Stack,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Tag,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    ArcElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from 'chart.js';
import Navbar from './Navbar';
import Loading from './Loading';
import AuthContext from '../context/AuthContext';
import { API_BASE_URL } from '../config/adminConfig';

ChartJS.register(ArcElement, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

const planColor = plan => {
    if (plan === 'business') return 'purple';
    if (plan === 'pro') return 'blue';
    return 'gray';
};

const roleLabel = role => ({
    rm: 'RM',
    compliance: 'Compliance',
    head: 'Head',
    portfolio_adviser: 'Portfolio Adviser',
    view_only: 'View Only',
}[role] || role);

function B2BUserMetrics() {
    const { token, logout } = useContext(AuthContext);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [planFilter, setPlanFilter] = useState('all');
    const [datasetFilter, setDatasetFilter] = useState('all');
    const cardBg = useColorModeValue('white', 'gray.800');
    const pageBg = useColorModeValue('gray.50', 'gray.900');

    useEffect(() => {
        const fetchB2BMetrics = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API_BASE_URL}/api/admin/b2b-users-summary`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setPayload(response.data);
                setError(null);
            } catch (err) {
                if (err.response?.status === 401) {
                    logout();
                    return;
                }
                setError(err.response?.data?.message || 'Unable to load B2B user metrics.');
            } finally {
                setLoading(false);
            }
        };

        fetchB2BMetrics();
        // logout is memoized in AuthContext; this effect should only refetch when the token changes.
    }, [token, logout]);

    const tenants = useMemo(() => payload?.tenants || [], [payload]);
    const summary = payload?.summary || {};

    const filteredTenants = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return tenants.filter(tenant => {
            const matchesSearch =
                !normalizedSearch ||
                tenant.name?.toLowerCase().includes(normalizedSearch) ||
                tenant.email?.toLowerCase().includes(normalizedSearch) ||
                tenant.businessName?.toLowerCase().includes(normalizedSearch) ||
                tenant.subdomain?.toLowerCase().includes(normalizedSearch);

            const matchesPlan = planFilter === 'all' || tenant.subscription === planFilter;
            const matchesDataset =
                datasetFilter === 'all' ||
                (datasetFilter === 'uploaded' && Number(tenant.noOfFileUploads || 0) > 0) ||
                (datasetFilter === 'missing' && Number(tenant.noOfFileUploads || 0) === 0);

            return matchesSearch && matchesPlan && matchesDataset;
        });
    }, [tenants, search, planFilter, datasetFilter]);

    const subscriptionData = {
        labels: Object.keys(summary.subscriptionTotals || {}),
        datasets: [{
            data: Object.values(summary.subscriptionTotals || {}),
            backgroundColor: ['#64748B', '#2563EB', '#7C3AED'],
            borderWidth: 2,
            borderColor: '#fff',
        }],
    };

    const registrationLabels = Object.keys(summary.monthlyRegistrations || {}).sort();
    const registrationData = {
        labels: registrationLabels,
        datasets: [{
            label: 'B2B tenants',
            data: registrationLabels.map(label => summary.monthlyRegistrations[label]),
            borderColor: '#2563EB',
            backgroundColor: 'rgba(37, 99, 235, 0.12)',
            tension: 0.4,
            fill: true,
        }],
    };

    const exportToCSV = () => {
        const headers = ['Business', 'Admin Email', 'Plan', 'Verified', 'Employees', 'Uploads', 'Subdomain'];
        const rows = filteredTenants.map(tenant => [
            tenant.businessName || tenant.name || '',
            tenant.email || '',
            tenant.subscription || '',
            tenant.verified ? 'Verified' : 'Unverified',
            tenant.employeeSeats || 0,
            tenant.noOfFileUploads || 0,
            tenant.subdomain || '',
        ]);
        const csvContent = [headers, ...rows]
            .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'b2b-users.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Box>
            <Navbar />
            <Box minH="100vh" bg={pageBg} p={6}>
                <Box maxW="7xl" mx="auto">
                    <HStack justify="space-between" align="start" mb={8}>
                        <Box>
                            <Heading size="xl">B2B User Metrics</Heading>
                            <Text mt={2} color="gray.600">
                                Tenant, plan, dataset, and role-seat metrics from the B2B platform.
                            </Text>
                        </Box>
                        {payload?.updatedAt ? (
                            <Tag colorScheme="blue">Updated {new Date(payload.updatedAt).toLocaleString()}</Tag>
                        ) : null}
                    </HStack>

                    {error ? (
                        <Card bg="red.50" borderWidth="1px" borderColor="red.200" mb={8}>
                            <CardBody>
                                <Text color="red.700">{error}</Text>
                            </CardBody>
                        </Card>
                    ) : null}

                    <SimpleGrid columns={[1, 2, 4]} spacing={6} mb={8}>
                        <StatCard label="B2B Tenants" value={summary.totalTenants || 0} color="gray.900" />
                        <StatCard label="Employee Seats" value={summary.totalEmployees || 0} color="purple.500" />
                        <StatCard
                            label="Tenants With Dataset"
                            value={summary.tenantsWithDataset || 0}
                            color="green.500"
                            help={`${summary.totalUploads || 0} total uploads`}
                        />
                        <StatCard label="Active Trials" value={summary.activeTrials || 0} color="blue.500" />
                    </SimpleGrid>

                    <SimpleGrid columns={[1, 2]} spacing={6} mb={8}>
                        <ChartCard title="B2B Plan Mix">
                            <Doughnut data={subscriptionData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </ChartCard>
                        <ChartCard title="B2B Tenant Registrations">
                            <Line data={registrationData} options={{ responsive: true, maintainAspectRatio: false }} />
                        </ChartCard>
                    </SimpleGrid>

                    <Card bg={cardBg} shadow="md">
                        <CardHeader display="flex" justifyContent="space-between" alignItems="center">
                            <Heading size="md">B2B Tenant Directory</Heading>
                            <Button colorScheme="blue" size="sm" onClick={exportToCSV}>
                                Export CSV
                            </Button>
                        </CardHeader>
                        <CardBody overflowX="auto">
                            <Stack direction={['column', 'row']} spacing={4} mb={4}>
                                <Input
                                    placeholder="Search business, admin, email, subdomain..."
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                />
                                <Select value={planFilter} onChange={(event) => setPlanFilter(event.target.value)}>
                                    <option value="all">All Plans</option>
                                    <option value="free">Free</option>
                                    <option value="pro">Pro</option>
                                    <option value="business">Business</option>
                                </Select>
                                <Select value={datasetFilter} onChange={(event) => setDatasetFilter(event.target.value)}>
                                    <option value="all">All Dataset States</option>
                                    <option value="uploaded">Dataset Uploaded</option>
                                    <option value="missing">No Dataset</option>
                                </Select>
                            </Stack>

                            <Table variant="striped" colorScheme="gray">
                                <Thead>
                                    <Tr>
                                        <Th>Business</Th>
                                        <Th>Admin Email</Th>
                                        <Th>Plan</Th>
                                        <Th>Dataset</Th>
                                        <Th>Employees</Th>
                                        <Th>Roles</Th>
                                        <Th>Subdomain</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {filteredTenants.map(tenant => (
                                        <Tr key={tenant.id}>
                                            <Td>{tenant.businessName || tenant.name || 'n/a'}</Td>
                                            <Td>{tenant.email}</Td>
                                            <Td>
                                                <Tag colorScheme={planColor(tenant.subscription)}>
                                                    {tenant.subscription}
                                                </Tag>
                                            </Td>
                                            <Td>
                                                <Tag colorScheme={tenant.noOfFileUploads > 0 ? 'green' : 'yellow'}>
                                                    {tenant.noOfFileUploads > 0 ? `${tenant.noOfFileUploads} uploads` : 'No upload'}
                                                </Tag>
                                            </Td>
                                            <Td>{tenant.employeeSeats}</Td>
                                            <Td>
                                                <HStack spacing={1} flexWrap="wrap">
                                                    {Object.entries(tenant.roleBreakdown || {}).map(([role, count]) => (
                                                        <Tag key={role} size="sm" colorScheme="blue">
                                                            {roleLabel(role)} {count}
                                                        </Tag>
                                                    ))}
                                                </HStack>
                                            </Td>
                                            <Td>{tenant.subdomain || '-'}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </CardBody>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
}

function StatCard({ label, value, color, help }) {
    return (
        <Card shadow="md">
            <CardBody>
                <Stat>
                    <StatLabel>{label}</StatLabel>
                    <StatNumber color={color}>{value}</StatNumber>
                    {help && <StatHelpText>{help}</StatHelpText>}
                </Stat>
            </CardBody>
        </Card>
    );
}

function ChartCard({ title, children }) {
    const cardBg = useColorModeValue('white', 'gray.800');
    return (
        <Card bg={cardBg} shadow="md">
            <CardHeader>
                <Heading size="md">{title}</Heading>
            </CardHeader>
            <CardBody h="320px">{children}</CardBody>
        </Card>
    );
}

export default B2BUserMetrics;
